import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { clearCart } from "../redux/cartSlice";
import { addOrder } from "../redux/ordersSlice";
import { useNavigate } from "react-router-dom";
import { getProductImage } from "../utils/getProductImage";
import { API_BASE_URL } from "../config/apiConfig";
import { motion } from "framer-motion";
import {
  FaShoppingBag,
  FaTruck,
  FaCheckCircle,
  FaLock,
} from "react-icons/fa";

const parsePrice = (price) => {
  if (typeof price === "number") return price;
  if (typeof price === "string") {
    const n = parseFloat(price.replace(/[^\d.-]/g, ""));
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
};

const FIELDS = [
  { name: "fullName",    label: "Full Name",      type: "text",  required: true },
  { name: "email",       label: "Email Address",  type: "email", required: true },
  { name: "phone",       label: "Phone Number",   type: "tel",   required: false },
  { name: "address",     label: "Address",        type: "text",  required: true },
  { name: "city",        label: "City",           type: "text",  required: true },
  { name: "postalCode",  label: "Postal Code",    type: "text",  required: false },
];

const CheckoutScreen = () => {
  const cartItems = useSelector((state) => state.cart.items);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    fullName: "", email: "", address: "",
    city: "", postalCode: "", paymentMethod: "cod", phone: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const orderPlacedRef = useRef(false);

  // Priority 8 (Step 9 roadmap): if the cart is empty and we're not mid-checkout
  // (loading stays true from setLoading(true) through the post-success navigate),
  // redirect to the cart page instead of showing an empty checkout form.
  // orderPlacedRef guards against a race with the success flow below: clearCart()
  // also empties the cart, which would otherwise re-trigger this effect and
  // overwrite the navigate("/thank-you") call with navigate("/cart").
  useEffect(() => {
    if (cartItems.length === 0 && !loading && !orderPlacedRef.current) {
      navigate("/cart", { replace: true });
    }
  }, [cartItems.length, loading, navigate]);

  const totalPrice = cartItems.reduce(
    (acc, item) => acc + parsePrice(item.price) * (item.quantity || 1), 0
  );
  const shippingFee = totalPrice > 0 ? 200 : 0;
  const grandTotal = totalPrice + shippingFee;

  const handleInputChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const validate = () => {
    const errs = {};
    if (!form.fullName.trim()) errs.fullName = "Full name is required";
    if (!form.email.trim()) errs.email = "Email is required";
    if (!form.address.trim()) errs.address = "Address is required";
    if (!form.city.trim()) errs.city = "City is required";
    return errs;
  };

  // Submit to adminhlc-api (MySQL) — Step 8 of the Firebase->PHP migration.
  // The order id is now server-assigned (MySQL AUTO_INCREMENT), not
  // generated client-side, so it's only known after a successful response.
  const handlePlaceOrder = async () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/public/orders/create.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userDetails: form,
          total: grandTotal,
          items: cartItems,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Failed to place order");
      }

      const orderData = {
        id: data.id, items: cartItems, total: grandTotal,
        userDetails: form, orderDate: new Date().toISOString(), status: "Pending",
      };
      orderPlacedRef.current = true;
      dispatch(addOrder(orderData));
      dispatch(clearCart());
      navigate("/thank-you", { replace: true });
    } catch (error) {
      console.error("Error placing order:", error);
      alert("❌ Failed to place order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800&family=DM+Sans:wght@300;400;500;600&display=swap');

        :root {
          --mg: #9b1f6c;
          --mg-dark: #7a1756;
          --mg-light: #f9eef5;
          --plum: #542757;
          --navy: #1a1830;
          --slate: #55536a;
          --line: #eceaf1;
          --bg: #f6f4fb;
          --white: #ffffff;
          --success: #16a34a;
          --error: #dc2626;
          --radius: 12px;
          --shadow: 0 4px 24px rgba(84,39,87,.10);
          --shadow-lg: 0 12px 40px rgba(84,39,87,.16);
        }

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        .co-page {
          font-family: 'DM Sans', system-ui, sans-serif;
          background: var(--bg);
          min-height: 100vh;
          padding: 0;
        }

        /* ── HEADER BAR ── */
        .co-header {
          background: var(--white);
          border-bottom: 1px solid var(--line);
          padding: 16px 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          position: sticky;
          top: 0;
          z-index: 100;
          box-shadow: 0 2px 12px rgba(0,0,0,.06);
        }
        .co-header__logo {
          font-family: 'Sora', sans-serif;
          font-size: 18px;
          font-weight: 800;
          color: var(--mg);
          letter-spacing: -.01em;
        }
        .co-header__secure {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          font-weight: 600;
          color: var(--slate);
        }
        .co-header__secure svg { color: var(--success); }

        /* ── STEPS BAR ── */
        .co-steps {
          background: var(--white);
          border-bottom: 1px solid var(--line);
          padding: 12px 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0;
        }
        .co-step {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          font-weight: 600;
          color: #bbb;
        }
        .co-step.active { color: var(--mg); }
        .co-step.done { color: var(--success); }
        .co-step__num {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          border: 2px solid currentColor;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 700;
          flex-shrink: 0;
        }
        .co-step.active .co-step__num { background: var(--mg); color: #fff; border-color: var(--mg); }
        .co-step__sep {
          width: 32px;
          height: 2px;
          background: var(--line);
          margin: 0 8px;
          flex-shrink: 0;
        }
        @media (max-width: 400px) {
          .co-step__label { display: none; }
          .co-step__sep { width: 20px; }
        }

        /* ── MAIN LAYOUT ── */
        .co-main {
          max-width: 1100px;
          margin: 0 auto;
          padding: 28px 16px 60px;
          display: grid;
          grid-template-columns: 1fr 380px;
          gap: 24px;
          align-items: start;
        }

        /* ── CARD ── */
        .co-card {
          background: var(--white);
          border-radius: var(--radius);
          border: 1px solid var(--line);
          box-shadow: var(--shadow);
          overflow: hidden;
        }
        .co-card__head {
          padding: 18px 22px 16px;
          border-bottom: 1px solid var(--line);
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .co-card__head-icon {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          background: var(--mg-light);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--mg);
          font-size: 15px;
          flex-shrink: 0;
        }
        .co-card__head h2 {
          font-family: 'Sora', sans-serif;
          font-size: 15px;
          font-weight: 700;
          color: var(--navy);
        }
        .co-card__head p {
          font-size: 11px;
          color: var(--slate);
          margin-top: 1px;
        }
        .co-card__body { padding: 22px; }

        /* ── FORM FIELDS ── */
        .co-form-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 14px;
        }
        .co-field { display: flex; flex-direction: column; gap: 5px; }
        .co-field.full { grid-column: 1 / -1; }
        .co-field label {
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: .07em;
          color: var(--slate);
        }
        .co-field label .req { color: var(--mg); margin-left: 2px; }
        .co-field input,
        .co-field select {
          width: 100%;
          padding: 11px 14px;
          border: 1.5px solid var(--line);
          border-radius: 8px;
          font-family: 'DM Sans', sans-serif;
          font-size: 14px;
          color: var(--navy);
          background: #fafafa;
          transition: border-color .2s, box-shadow .2s;
          outline: none;
          appearance: auto;
        }
        .co-field input:focus,
        .co-field select:focus {
          border-color: var(--mg);
          background: var(--white);
          box-shadow: 0 0 0 3px rgba(155,31,108,.10);
        }
        .co-field input.error,
        .co-field select.error { border-color: var(--error); }
        .co-field__error {
          font-size: 11px;
          color: var(--error);
          font-weight: 500;
        }
        .co-field input::placeholder { color: #b0aec0; }

        /* Payment method */
        .co-payment-opts { display: flex; flex-direction: column; gap: 10px; margin-top: 6px; }
        .co-payment-opt {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 13px 16px;
          border: 1.5px solid var(--line);
          border-radius: 8px;
          cursor: pointer;
          transition: border-color .2s, background .2s;
        }
        .co-payment-opt.selected {
          border-color: var(--mg);
          background: var(--mg-light);
        }
        .co-payment-opt input[type="radio"] { accent-color: var(--mg); width: 16px; height: 16px; }
        .co-payment-opt__label {
          font-size: 13px;
          font-weight: 600;
          color: var(--navy);
        }
        .co-payment-opt__desc { font-size: 11px; color: var(--slate); margin-top: 1px; }

        /* Section divider */
        .co-section-title {
          font-family: 'Sora', sans-serif;
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: .1em;
          color: var(--slate);
          margin: 20px 0 14px;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .co-section-title::after {
          content: '';
          flex: 1;
          height: 1px;
          background: var(--line);
        }

        /* ── ORDER SUMMARY (right col) ── */
        .co-summary { position: sticky; top: 80px; }

        .co-items-list { display: flex; flex-direction: column; gap: 0; }
        .co-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 0;
          border-bottom: 1px solid var(--line);
        }
        .co-item:last-child { border-bottom: none; }
        .co-item__img {
          width: 48px;
          height: 48px;
          border-radius: 8px;
          background: var(--bg);
          border: 1px solid var(--line);
          overflow: hidden;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          color: #bbb;
        }
        .co-item__img img { width: 100%; height: 100%; object-fit: contain; }
        .co-item__info { flex: 1; min-width: 0; }
        .co-item__name {
          font-size: 13px;
          font-weight: 600;
          color: var(--navy);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .co-item__qty { font-size: 11px; color: var(--slate); margin-top: 2px; }
        .co-item__price {
          font-size: 13px;
          font-weight: 700;
          color: var(--navy);
          flex-shrink: 0;
        }

        .co-totals { margin-top: 4px; }
        .co-total-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 9px 0;
          font-size: 13px;
          color: var(--slate);
          border-bottom: 1px solid var(--line);
        }
        .co-total-row:last-child { border-bottom: none; }
        .co-total-row.grand {
          font-size: 16px;
          font-weight: 800;
          color: var(--navy);
          padding: 14px 0 0;
        }
        .co-total-row.grand .amount { color: var(--mg); }

        .co-empty {
          text-align: center;
          padding: 32px 0;
          color: var(--slate);
          font-size: 14px;
        }
        .co-empty svg { font-size: 32px; color: #ddd; margin-bottom: 8px; }

        /* ── CTA BUTTON ── */
        .co-cta {
          width: 100%;
          padding: 15px;
          background: linear-gradient(135deg, var(--plum), var(--mg));
          color: #fff;
          border: none;
          border-radius: 10px;
          font-family: 'Sora', sans-serif;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: filter .2s, transform .15s;
          box-shadow: 0 4px 16px rgba(155,31,108,.30);
          margin-top: 18px;
          letter-spacing: .01em;
        }
        .co-cta:hover:not(:disabled) { filter: brightness(1.08); transform: translateY(-1px); }
        .co-cta:active { transform: scale(.98); }
        .co-cta:disabled { opacity: .6; cursor: not-allowed; }

        .co-cta-note {
          text-align: center;
          font-size: 11px;
          color: var(--slate);
          margin-top: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 5px;
        }

        /* ── MOBILE STICKY FOOTER ── */
        .co-mobile-total {
          display: none;
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          background: var(--white);
          border-top: 1px solid var(--line);
          padding: 12px 16px;
          z-index: 99;
          box-shadow: 0 -4px 20px rgba(0,0,0,.10);
        }
        .co-mobile-total__row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 10px;
        }
        .co-mobile-total__label { font-size: 13px; color: var(--slate); font-weight: 500; }
        .co-mobile-total__amount {
          font-family: 'Sora', sans-serif;
          font-size: 18px;
          font-weight: 800;
          color: var(--mg);
        }

        /* ── RESPONSIVE ── */
        @media (max-width: 900px) {
          .co-main {
            display: flex;
            flex-direction: column;
            padding: 16px 12px 120px;
            gap: 16px;
          }

          /* Both cards take full width */
          .co-main > * {
            width: 100%;
            min-width: 0;
          }

          /* Summary comes first on mobile */
          .co-summary {
            position: static;
            order: -1;
            width: 100%;
          }

          .co-mobile-total { display: block; }
        }

        @media (max-width: 540px) {
          .co-form-grid { grid-template-columns: 1fr; }
          .co-field.full { grid-column: 1; }
          .co-header { padding: 14px 16px; }
          .co-card__body { padding: 16px; }
          .co-card__head { padding: 14px 16px 12px; }
        }

        /* Loading spinner */
        @keyframes spin { to { transform: rotate(360deg); } }
        .co-spinner {
          width: 18px;
          height: 18px;
          border: 2px solid rgba(255,255,255,.4);
          border-top-color: #fff;
          border-radius: 50%;
          animation: spin .7s linear infinite;
        }
      `}</style>

      <div className="co-page">
        {/* ── HEADER ── */}
        <header className="co-header">
          <div className="co-header__logo">HLC Checkout</div>
          <div className="co-header__secure">
            <FaLock size={12} style={{ color: 'var(--success)' }} />
            Secure Checkout
          </div>
        </header>

        {/* ── STEPS ── */}
        <div className="co-steps">
          <div className="co-step done">
            <div className="co-step__num">✓</div>
            <span className="co-step__label">Cart</span>
          </div>
          <div className="co-step__sep" />
          <div className="co-step active">
            <div className="co-step__num">2</div>
            <span className="co-step__label">Details</span>
          </div>
          <div className="co-step__sep" />
          <div className="co-step">
            <div className="co-step__num">3</div>
            <span className="co-step__label">Confirm</span>
          </div>
        </div>

        {/* ── MAIN ── */}
        <div className="co-main">

          {/* LEFT — Shipping Form */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
          >
            <div className="co-card">
              <div className="co-card__head">
                <div className="co-card__head-icon"><FaTruck /></div>
                <div>
                  <h2>Shipping Information</h2>
                  <p>Tell us where to deliver your order</p>
                </div>
              </div>
              <div className="co-card__body">
                <div className="co-form-grid">
                  {FIELDS.map((field) => (
                    <div
                      key={field.name}
                      className={`co-field ${field.name === "address" ? "full" : ""}`}
                    >
                      <label>
                        {field.label}
                        {field.required && <span className="req">*</span>}
                      </label>
                      <input
                        type={field.type}
                        name={field.name}
                        placeholder={field.label}
                        value={form[field.name]}
                        onChange={handleInputChange}
                        className={errors[field.name] ? "error" : ""}
                        autoComplete="on"
                      />
                      {errors[field.name] && (
                        <span className="co-field__error">{errors[field.name]}</span>
                      )}
                    </div>
                  ))}
                </div>

                <div className="co-section-title">Payment Method</div>

                <div className="co-payment-opts">
                  <label
                    className={`co-payment-opt ${form.paymentMethod === "cod" ? "selected" : ""}`}
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="cod"
                      checked={form.paymentMethod === "cod"}
                      onChange={handleInputChange}
                    />
                    <div>
                      <div className="co-payment-opt__label">💵 Cash on Delivery</div>
                      <div className="co-payment-opt__desc">Pay when your order arrives</div>
                    </div>
                  </label>
                </div>

                {/* CTA — desktop only inside form */}
                <motion.button
                  className="co-cta"
                  onClick={handlePlaceOrder}
                  disabled={loading || cartItems.length === 0}
                  whileTap={{ scale: 0.97 }}
                  style={{ display: 'flex' }}
                >
                  {loading
                    ? <><div className="co-spinner" /> Processing…</>
                    : <><FaCheckCircle /> Place Order — ₨ {grandTotal.toLocaleString()}</>
                  }
                </motion.button>

                <p className="co-cta-note">
                  <FaLock size={10} /> Your information is 100% secure
                </p>
              </div>
            </div>
          </motion.div>

          {/* RIGHT — Order Summary */}
          <motion.div
            className="co-summary"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.1 }}
          >
            <div className="co-card">
              <div className="co-card__head">
                <div className="co-card__head-icon"><FaShoppingBag /></div>
                <div>
                  <h2>Order Summary</h2>
                  <p>{cartItems.length} item{cartItems.length !== 1 ? "s" : ""} in your cart</p>
                </div>
              </div>
              <div className="co-card__body">
                {cartItems.length === 0 ? (
                  <div className="co-empty">
                    <div><FaShoppingBag /></div>
                    Your cart is empty
                  </div>
                ) : (
                  <>
                    <div className="co-items-list">
                      {cartItems.map((item) => (
                        <div key={item.id} className="co-item">
                          <div className="co-item__img">
                            <img src={getProductImage(item)} alt={item.name} />
                          </div>
                          <div className="co-item__info">
                            <div className="co-item__name">{item.name}</div>
                            <div className="co-item__qty">Qty: {item.quantity || 1}</div>
                          </div>
                          <div className="co-item__price">
                            ₨ {(parsePrice(item.price) * (item.quantity || 1)).toLocaleString()}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="co-totals">
                      <div className="co-total-row">
                        <span>Subtotal</span>
                        <span>₨ {totalPrice.toLocaleString()}</span>
                      </div>
                      <div className="co-total-row">
                        <span>Shipping</span>
                        <span>{shippingFee > 0 ? `₨ ${shippingFee.toLocaleString()}` : "Free"}</span>
                      </div>
                      <div className="co-total-row grand">
                        <span>Grand Total</span>
                        <span className="amount">₨ {grandTotal.toLocaleString()}</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </motion.div>

        </div>

        {/* ── MOBILE STICKY FOOTER ── */}
        <div className="co-mobile-total">
          <div className="co-mobile-total__row">
            <span className="co-mobile-total__label">Grand Total</span>
            <span className="co-mobile-total__amount">₨ {grandTotal.toLocaleString()}</span>
          </div>
          <motion.button
            className="co-cta"
            style={{ marginTop: 0 }}
            onClick={handlePlaceOrder}
            disabled={loading || cartItems.length === 0}
            whileTap={{ scale: 0.97 }}
          >
            {loading
              ? <><div className="co-spinner" /> Processing…</>
              : <><FaCheckCircle /> Place Order</>
            }
          </motion.button>
        </div>

      </div>
    </>
  );
};

export default CheckoutScreen;